import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_REPORT_SERVICE } from '@/api/constant';
import DashboardCard from '@/components/common/Dashboard/DashboardCard';
import DashboardCharts from '@/components/common/Dashboard/DashboardCharts';
import Layout from '@/components/Layout/Layout';
import RecentOrders from '@/components/order/RecentOrders';
import DateRangePicker from '@/components/UI/DateRange';
import FormField from '@/components/UI/FormField';
import { IDashboardResponse } from '@/interfaces/dashboard.interface';
import {
  formatDateTime,
  formatPlaceholder,
  GetFilterUrl,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { Range } from 'react-date-range';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TopProducts from '@/components/product/TopProducts';
import DashboardSkeleton from '@/components/UI/loaders/CommonSkeleton';
import TopCustomersList from '@/components/dashboard/TopCustomer';
import Card from '@/components/UI/Card';
import AreaGrowthGraph from '@/components/UI/Chart/LineChart';

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<IDashboardResponse | null>(
    null,
  );
  const [selectedValue, setSelectedValue] = useState('week');
  const today = new Date();
  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(new Date().getFullYear(), today.getMonth(), 1),
      endDate: today,
      key: 'selection',
    },
  ]);

  const filterTypes = [
    { label: 'Custom', value: 'custom' },
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
  ];

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      if (!dateRange?.[0]?.startDate || !dateRange?.[0]?.endDate) {
        return;
      }
      const startDate =
        selectedValue == 'custom' || selectedValue === ''
          ? formatDateTime(dateRange[0].startDate, 'YYMMDD')
          : '';
      const endDate =
        selectedValue == 'custom' || selectedValue === ''
          ? formatDateTime(dateRange[0].endDate, 'YYMMDD')
          : '';
      const filterQuery = {
        from: startDate,
        to: endDate,
        type: selectedValue,
      };

      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_REPORT_SERVICE}${API_ENDPOINT.dashboard}?${query ? query : ''}`;
      const res = await callApi<IDashboardResponse>(PATH, { method: 'GET' });
      setLoading(false);
      setDashboardData(res);
    } catch (error) {
      setLoading(false);
      ThrowError(error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedValue]);

  const dashboardCards = [
    {
      title: 'Total Sales',
      value: `BHD ${toFixedThree(dashboardData?.orderAnalytics?.totalSales?.amount || 0)}`,
      subtitle: formatPlaceholder(
        dashboardData?.orderAnalytics?.totalSales?.type,
      ),
      percent: dashboardData?.orderAnalytics?.totalSales?.comparison?.change,
      percentColor:
        dashboardData?.orderAnalytics?.totalSales?.comparison?.direction ===
        'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: './assets/images/dashboard-card-img-1.svg',
    },
    {
      title: 'New Users',
      value: dashboardData?.userAnalytics?.newUserCount?.amount || 0,
      subtitle: formatPlaceholder(
        dashboardData?.userAnalytics?.newUserCount?.type,
      ),
      percent: dashboardData?.userAnalytics?.newUserCount?.comparison?.change,
      percentColor: 'text-green-500',
      img: './assets/images/dashboard-card-img-2.svg',
    },
    {
      title: 'Total Orders',
      value: dashboardData?.orderAnalytics?.totalOrders?.amount || 0,
      subtitle: formatPlaceholder(
        dashboardData?.orderAnalytics?.totalOrders?.type,
      ),
      percent: dashboardData?.orderAnalytics?.totalOrders?.comparison?.change,
      percentColor:
        dashboardData?.orderAnalytics?.totalOrders?.comparison?.direction ===
        'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: './assets/images/dashboard-card-img-3.svg',
      to: RoutesLink.order,
    },
    {
      title: 'Customer Refunds',
      value: `BHD ${toFixedThree(dashboardData?.orderAnalytics?.refundAmount?.amount || 0)}`,
      subtitle: formatPlaceholder(
        dashboardData?.orderAnalytics?.refundAmount?.type,
      ),
      percent: dashboardData?.orderAnalytics?.refundAmount?.comparison?.change,
      percentColor:
        dashboardData?.orderAnalytics?.refundAmount?.comparison?.direction ===
        'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: './assets/images/dashboard-card-img-4.svg',
      to: RoutesLink.refundReturn,
    },
    {
      title: 'Total Users',
      value: dashboardData?.userAnalytics?.allUserCount?.amount || 0,
      img: './assets/images/dashboard-card-img-5.svg',
    },
    {
      title: 'Points Redeemed',
      value: dashboardData?.loyaltyAnalytics?.pointsRedeemed?.amount || 0,
      subtitle: formatPlaceholder(
        dashboardData?.loyaltyAnalytics?.pointsRedeemed?.type,
      ),
      percent:
        dashboardData?.loyaltyAnalytics?.pointsRedeemed?.comparison?.change,
      percentColor:
        dashboardData?.loyaltyAnalytics?.pointsRedeemed?.comparison
          ?.direction === 'increase'
          ? 'text-green-500'
          : 'text-red-500',
      img: './assets/images/dashboard-card-img-6.svg',
    },
  ];

  return (
    <Layout>
      {loading ? (
        <>
          <DashboardSkeleton />
        </>
      ) : (
        <>
          <div className="mx-auto space-y-5">
            <div className="mb-8 flex justify-between flex-wrap gap-5">
              <div>
                <h2 className="text-3xl mb-2 font-bold text-gray-800">
                  Welcome Back
                </h2>
                <p className="text-gray-100">
                  Here's a quick overview of your store's activity.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(selectedValue === 'custom' || selectedValue === '') && (
                  <>
                    <DateRangePicker
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={fetchDashboardData}
                    >
                      Apply Filter
                    </button>
                  </>
                )}
                <FormField
                  type="select"
                  isHidden
                  options={filterTypes}
                  value={selectedValue}
                  onChange={(e) => setSelectedValue(e.target.value)}
                />
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5"
              uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
            >
              {dashboardCards.map((card) => (
                <DashboardCard
                  img={card?.img}
                  percent={card?.percent}
                  percentColor={card?.percentColor}
                  subtitle={card?.subtitle}
                  title={card?.title}
                  value={card?.value}
                  key={card.title}
                  to={card.to}
                />
              ))}
            </div>
            {/* <!-- Recent Orders --> */}
            {dashboardData && dashboardData.recentOrders.length && (
              <div uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100">
                <div className="card-1 shadow-1">
                  <div className="flex flex-1 items-center justify-between mb-6 gap-3 flex-wrap">
                    <h3 className={`text-base text-black font-semibold`}>
                      Recent Orders
                    </h3>
                    <Link className="btn-primary" to={RoutesLink.order}>
                      View All
                    </Link>
                  </div>
                  <RecentOrders
                    orders={dashboardData.recentOrders}
                    onSuccess={() => fetchDashboardData()}
                  />
                </div>
              </div>
            )}
            {dashboardData && (
              <div uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100">
                <div className="card-1 shadow-1">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base text-black">
                      Low Quantity Alerts
                    </h3>
                    <Link to={RoutesLink.products} className="btn-primary">
                      View all
                    </Link>
                  </div>

                  <TopProducts
                    emptyMessage="No Products In The Low Quantity"
                    products={dashboardData.lowStockProducts}
                  />
                </div>
              </div>
            )}
            {/* <!-- Top Products Orders --> */}
            <div
              className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-5 mb-5"
              uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100"
            >
              {dashboardData && dashboardData.topProducts.length && (
                <div uk-scrollspy="target: > div; cls: uk-animation-slide-bottom-small; delay: 100">
                  <div className="card-1 shadow-1">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base text-black">Top Products</h3>
                      <Link to={RoutesLink.products} className="btn-primary">
                        View all
                      </Link>
                    </div>

                    <TopProducts products={dashboardData.topProducts} />
                  </div>
                </div>
              )}
              <DashboardCharts
                salesByCategory={
                  dashboardData?.orderGrpahs?.salesByCategory || []
                }
              />
            </div>

            <div className="my-6">
              <div className="mb-3">
                {dashboardData?.topCustomers &&
                  Object.keys(dashboardData.topCustomers).length > 0 && (
                    <TopCustomersList
                      topCustomers={dashboardData.topCustomers}
                    />
                  )}
              </div>
            </div>
          </div>
          <Card>
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-base text-black">Revenue Growth</h3>
            </div>
            <div>
              <AreaGrowthGraph
                data={dashboardData?.orderGrpahs?.revenueGrowth || []}
              />
            </div>
          </Card>
        </>
      )}
    </Layout>
  );
};

export default Dashboard;
